import { OrganizationCloseStage } from "@common/types/organizationCloseStage";
import { ClientsWorkOverviewWorkRow } from "./clientsWorkOverviewViewConfig";
import type { WorkRowGroup } from "./workRowGroups";

export const NO_STAGE_GROUP_ID = "stage:none";
export const DONE_STAGE_GROUP_ID = "stage:done";
export const CANCELED_STAGE_GROUP_ID = "stage:canceled";

export const getStageGroupId = (stageId: string) => `stage:${stageId}`;

export type WorkFinishedState = "canceled" | "done";

export const WORK_FINISHED_STATE_TITLES: Record<WorkFinishedState, string> = {
    canceled: "Canceled",
    done: "Done",
};

const FINISHED_STATE_GROUP_IDS: Record<WorkFinishedState, string> = {
    canceled: CANCELED_STAGE_GROUP_ID,
    done: DONE_STAGE_GROUP_ID,
};

/**
 * Whether work is canceled, done, or still open (null). Canceled wins over
 * done, and either wins over the stage, wherever work's stage is shown.
 */
export const getWorkFinishedState = (
    row: Pick<ClientsWorkOverviewWorkRow, "cancelledAt" | "isFinished">,
): WorkFinishedState | null => {
    if (row.cancelledAt) {
        return "canceled";
    }
    return row.isFinished ? "done" : null;
};

export interface OrderedCloseStage {
    stage: OrganizationCloseStage;
    title: string;
}

const byOrder = (a: OrganizationCloseStage, b: OrganizationCloseStage) =>
    a.order - b.order;

/**
 * Stages in the order the close stage calculation walks them: each top-level
 * stage by `order`, followed by its children by `order`. `order` is only
 * meaningful among siblings. Children are titled "{parent} · {child}".
 */
export function getOrderedCloseStages(
    stages: readonly OrganizationCloseStage[],
): OrderedCloseStage[] {
    return stages
        .filter((stage) => stage.parentCloseStageId === null)
        .toSorted(byOrder)
        .flatMap((parent) => [
            { stage: parent, title: parent.name },
            ...stages
                .filter((stage) => stage.parentCloseStageId === parent.id)
                .toSorted(byOrder)
                .map((child) => ({
                    stage: child,
                    title: `${parent.name} · ${child.name}`,
                })),
        ]);
}

const createGroup = (id: string, title: string): WorkRowGroup => ({
    id,
    title,
    rows: [],
});

const getRowGroupId = (row: ClientsWorkOverviewWorkRow) => {
    const finishedState = getWorkFinishedState(row);
    if (finishedState) {
        return FINISHED_STATE_GROUP_IDS[finishedState];
    }
    return row.closeStageId
        ? getStageGroupId(row.closeStageId)
        : NO_STAGE_GROUP_ID;
};

/**
 * Work rows grouped by stage: the stages in calculation order, any stage
 * missing from `stages` (titled from the row), "Done", "Canceled", then
 * "No stage" last. A finished or canceled row only lands in Done or Canceled.
 * Every group is returned, empty ones included, and rows keep their input
 * order; callers drop empty groups and sort rows as their layout needs.
 */
export function getWorkStageGroups({
    rows,
    stages,
}: {
    rows: readonly ClientsWorkOverviewWorkRow[];
    stages: readonly OrganizationCloseStage[];
}): WorkRowGroup[] {
    const noStageGroup = createGroup(NO_STAGE_GROUP_ID, "No stage");
    const stageGroups = getOrderedCloseStages(stages).map(({ stage, title }) =>
        createGroup(getStageGroupId(stage.id), title),
    );
    const doneGroup = createGroup(
        DONE_STAGE_GROUP_ID,
        WORK_FINISHED_STATE_TITLES.done,
    );
    const canceledGroup = createGroup(
        CANCELED_STAGE_GROUP_ID,
        WORK_FINISHED_STATE_TITLES.canceled,
    );
    const groupsById = new Map(
        [noStageGroup, ...stageGroups, doneGroup, canceledGroup].map(
            (group) => [group.id, group],
        ),
    );

    const unknownStageGroups: WorkRowGroup[] = [];
    for (const row of rows) {
        const groupId = getRowGroupId(row);
        let group = groupsById.get(groupId);
        if (!group) {
            group = createGroup(groupId, row.closeStageName ?? "Unknown stage");
            groupsById.set(groupId, group);
            unknownStageGroups.push(group);
        }
        group.rows.push(row);
    }

    return [
        ...stageGroups,
        ...unknownStageGroups,
        doneGroup,
        canceledGroup,
        noStageGroup,
    ];
}
